/**
 * DataFreshnessIndicator.tsx
 * Feature: 154 - Eventstream Real-Time Capacity Telemetry
 * Author: Peter Dobler
 * Date: 2026-05-18
 * Constitution Version: 1.12.0
 *
 * Shows event data age as a colored badge. Displays a degraded-mode banner
 * when streaming is unavailable.
 */

import React, { useEffect, useState } from 'react';
import { Badge, AppTooltip } from '../ui';
import type { BadgeVariant } from '../ui';
import { formatEastern } from '@/utils/dateFormatter';

interface DataFreshnessIndicatorProps {
  lastEventAt: string | null;
  degradedMode: boolean;
  /** If true, renders only the chip without the degraded banner */
  compact?: boolean;
}

function getAgeSeconds(lastEventAt: string | null): number | null {
  if (!lastEventAt) return null;
  return Math.floor((Date.now() - new Date(lastEventAt).getTime()) / 1000);
}

function formatAge(seconds: number): string {
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  return `${Math.floor(seconds / 3600)}h ago`;
}

type FreshnessLevel = 'fresh' | 'aging' | 'stale' | 'unknown';

function getFreshnessLevel(ageSeconds: number | null): FreshnessLevel {
  if (ageSeconds === null) return 'unknown';
  if (ageSeconds < 60) return 'fresh';
  if (ageSeconds < 300) return 'aging';
  return 'stale';
}

const LEVEL_COLORS: Record<FreshnessLevel, BadgeVariant> = {
  fresh: 'success',
  aging: 'warning',
  stale: 'error',
  unknown: 'default',
};

const LEVEL_LABELS: Record<FreshnessLevel, string> = {
  fresh: 'Live',
  aging: 'Aging',
  stale: 'Stale',
  unknown: 'No data',
};

export default function DataFreshnessIndicator({
  lastEventAt,
  degradedMode,
  compact = false,
}: DataFreshnessIndicatorProps) {
  const [, setTick] = useState(0);

  // Re-render every 10s so the age label stays current
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 10_000);
    return () => clearInterval(id);
  }, []);

  const ageSeconds = getAgeSeconds(lastEventAt);
  const level = getFreshnessLevel(ageSeconds);
  const color = LEVEL_COLORS[level];
  const label =
    ageSeconds !== null ? `${LEVEL_LABELS[level]} · ${formatAge(ageSeconds)}` : 'No data';

  return (
    <div className="flex flex-col gap-2">
      <AppTooltip
        title={
          lastEventAt ? `Last event: ${formatEastern(lastEventAt)}` : 'No events received'
        }
      >
        <Badge
          variant={color}
          isOutlined
          leftIcon={<span className="h-2 w-2 rounded-full bg-current" />}
        >
          {label}
        </Badge>
      </AppTooltip>
    </div>
  );
}
